import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { AppUrlConstants } from '../../../core/constants/AppUrlConstants';

interface ChatLog {
  chatLogId?: number;
  id?: number;
  sessionId?: string;
  userId?: string | number | null;
  question?: string;
  userQuestion?: string;
  answer?: string;
  matchedAnswer?: string;
  similarityScore?: number | null;
  createdAt: string;
}

interface SessionSummary {
  sessionId: string;
  messageCount: number;
  firstDate: string;
  lastDate: string;
  messages: ChatLog[];
}

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error';
}

@Component({
  selector: 'app-chat-logs',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './chat-logs.component.html',
  styleUrl: './chat-logs.component.css'
})
export class ChatLogsComponent implements OnInit {

  data: ChatLog[] = [];
  loading = true;
  search = '';
  fromDate = '';
  toDate = '';
  page = 1;
  rowsPerPage = 10;
  toasts: Toast[] = [];
  skeletonRows = Array.from({ length: 8 });

  // ─── Session Modal ───
  showSessionModal = false;
  selectedSessionId = '';
  sessionMessages: ChatLog[] = [];

  private toastIdCounter = 0;

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    this.fetchData();
  }

  getQuestion(row: ChatLog): string {
    return row.question || row.userQuestion || '';
  }

  getAnswer(row: ChatLog): string {
    return row.answer || row.matchedAnswer || '';
  }

  // ─── Group raw data into distinct sessions ───
  get sessions(): SessionSummary[] {
    const map = new Map<string, ChatLog[]>();

    for (const row of this.data) {
      const sid = row.sessionId || 'unknown';
      if (!map.has(sid)) map.set(sid, []);
      map.get(sid)!.push(row);
    }

    return Array.from(map.entries()).map(([sessionId, msgs]) => {
      const sorted = [...msgs].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
      return {
        sessionId,
        messageCount: msgs.length,
        firstDate: sorted[0]?.createdAt || '',
        lastDate: sorted[sorted.length - 1]?.createdAt || '',
        messages: sorted,
      };
    });
  }

  // ─── Filtered sessions ───
  get filtered(): SessionSummary[] {
    return this.sessions.filter(s => {
      const sid = s.sessionId.toLowerCase();
      const matchesSearch = !this.search || sid.includes(this.search.toLowerCase());

      let matchesDate = true;
      if (this.fromDate || this.toDate) {
        const d = s.lastDate ? new Date(s.lastDate) : null;
        if (d) {
          if (this.fromDate && d < new Date(this.fromDate)) matchesDate = false;
          if (this.toDate && d > new Date(this.toDate + 'T23:59:59')) matchesDate = false;
        }
      }
      return matchesSearch && matchesDate;
    });
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.filtered.length / this.rowsPerPage));
  }

  get pageData(): SessionSummary[] {
    return this.filtered.slice((this.page - 1) * this.rowsPerPage, this.page * this.rowsPerPage);
  }

  // ─── Fetch ───
  fetchData(): void {
    this.loading = true;
    this.http.get<any>(AppUrlConstants.GET_CHATLOGS).subscribe({
      next: (json) => {
        const list = Array.isArray(json) ? json : json.data || json.results || [];
        this.data = list;
        this.loading = false;
      },
      error: () => {
        this.addToast('Failed to load chat logs', 'error');
        this.data = [];
        this.loading = false;
      }
    });
  }

  // ─── Session Modal ───
  openSession(s: SessionSummary): void {
    this.selectedSessionId = s.sessionId;
    this.sessionMessages = s.messages;
    this.showSessionModal = true;
  }

  closeSession(): void {
    this.showSessionModal = false;
    this.selectedSessionId = '';
    this.sessionMessages = [];
  }

  // ─── Toast ───
  addToast(message: string, type: 'success' | 'error' = 'success'): void {
    const id = ++this.toastIdCounter;
    this.toasts.push({ id, message, type });
    setTimeout(() => this.removeToast(id), 3000);
  }

  removeToast(id: number): void {
    this.toasts = this.toasts.filter(t => t.id !== id);
  }

  // ─── Filters ───
  onSearchChange(): void { this.page = 1; }

  clearFilters(): void {
    this.search = '';
    this.fromDate = '';
    this.toDate = '';
    this.page = 1;
  }

  // ─── Helpers ───
  formatDate(iso: string): string {
    if (!iso) return '';
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  }
  // ─── Helpers for time ───
  formatTime(iso: string): string {
    if (!iso) return '';
    const d = new Date(iso);

    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');

    return `${hh}:${mm}`;
  }
  getRowClass(idx: number): string {
    return idx % 2 === 0 ? 'row-even' : 'row-odd';
  }

  getRowIndex(idx: number): number {
    return (this.page - 1) * this.rowsPerPage + idx + 1;
  }

  getAvgScore(s: SessionSummary): number {
    const scores = s.messages
      .map(m => m.similarityScore)
      .filter((v): v is number => v != null);
    if (scores.length === 0) return 0;
    return scores.reduce((sum, v) => sum + v, 0) / scores.length;
  }

  getUserType(s: SessionSummary): 'Registered' | 'Unregistered' {
    return s.messages.some(m => m.userId != null) ? 'Registered' : 'Unregistered';
  }

  shortSessionId(sessionId: string): string {
    if (!sessionId) return '—';
    return sessionId.length > 14 ? sessionId.slice(0, 14) + '…' : sessionId;
  }

  getDateLabel(dateStr: string): string {
  if (!dateStr) return '';

  const date = new Date(dateStr);
  const today = new Date();

  if (date.toDateString() === today.toDateString()) return 'Today';

  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';

  return date.toLocaleDateString('en-GB');
}

get groupedMessages() {
  const groups: Record<string, ChatLog[]> = {};

  for (const msg of this.sessionMessages) {
    const key = this.getDateLabel(msg.createdAt);

    if (!groups[key]) groups[key] = [];
    groups[key].push(msg);
  }

  return Object.entries(groups);
}
}
